import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { fetchExchangeRates } from "../services/api";
import {
  CURRENCY_STORAGE_KEY,
  DEFAULT_CURRENCY,
  FALLBACK_RATES_FROM_INR,
  LEGACY_CURRENCY_STORAGE_KEY,
  SUPPORTED_CURRENCIES,
  countryToCurrency,
  normalizeCurrency,
} from "../lib/currencyConfig";
import { detectCountryCode } from "../lib/geoLocation";
import { formatFromInr } from "../lib/currency";
import { isPrerenderOrSnap } from "../lib/isBotOrPrerender";

const CurrencyContext = createContext(null);

function readStoredOverride() {
  try {
    const override = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (override) return normalizeCurrency(override, null) || null;
    const legacy = localStorage.getItem(LEGACY_CURRENCY_STORAGE_KEY);
    if (legacy) {
      const normalized = normalizeCurrency(legacy, null);
      if (normalized) {
        localStorage.setItem(CURRENCY_STORAGE_KEY, normalized);
        return normalized;
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function CurrencyProvider({ children }) {
  const skipNetwork = isPrerenderOrSnap();

  const [currency, setCurrencyState] = useState(
    () => (skipNetwork ? DEFAULT_CURRENCY : readStoredOverride() || DEFAULT_CURRENCY)
  );
  const [rates, setRates] = useState(FALLBACK_RATES_FROM_INR);
  const [isDetecting, setIsDetecting] = useState(
    () => !skipNetwork && !readStoredOverride()
  );
  const [ratesReady, setRatesReady] = useState(skipNetwork);

  useEffect(() => {
    if (skipNetwork) {
      setRates(FALLBACK_RATES_FROM_INR);
      setRatesReady(true);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const data = await fetchExchangeRates();
      if (cancelled) return;
      if (data?.rates && typeof data.rates === "object") {
        setRates({ INR: 1, ...data.rates });
      }
      setRatesReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [skipNetwork]);

  useEffect(() => {
    if (skipNetwork) {
      setCurrencyState(DEFAULT_CURRENCY);
      setIsDetecting(false);
      return undefined;
    }

    const override = readStoredOverride();
    if (override) {
      setCurrencyState(override);
      setIsDetecting(false);
      return undefined;
    }

    let cancelled = false;
    setIsDetecting(true);

    (async () => {
      const country = await detectCountryCode();
      if (cancelled) return;
      setCurrencyState(countryToCurrency(country));
      setIsDetecting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [skipNetwork]);

  const setCurrency = (code) => {
    const next = normalizeCurrency(code, DEFAULT_CURRENCY);
    setCurrencyState(next);
    try {
      localStorage.setItem(CURRENCY_STORAGE_KEY, next);
      localStorage.setItem(LEGACY_CURRENCY_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  };

  const value = useMemo(
    () => ({
      currency,
      setCurrency,
      rates,
      ratesReady,
      isDetecting,
      supportedCurrencies: SUPPORTED_CURRENCIES,
      formatPrice: (amountInr) => formatFromInr(amountInr, currency, rates),
    }),
    [currency, rates, ratesReady, isDetecting]
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    throw new Error("useCurrency must be used within CurrencyProvider");
  }
  return ctx;
}
